
import { ApiClient } from "./client";
import { supabase } from "@/integrations/supabase/client";

class CreatorsApi extends ApiClient {
  /**
   * Fetch all creators
   */
  async getAllCreators() {
    return this.executeQuery(
      async () => await supabase
        .from('profiles')
        .select('*')
        .eq('role', 'creator')
        .order('full_name'),
      "Failed to fetch creators"
    );
  }
  
  /**
   * Fetch a creator by ID
   */
  async getCreatorById(id: string) {
    return this.executeQuery(
      async () => await supabase
        .from('profiles')
        .select('*')
        .eq('id', id)
        .maybeSingle(),
      `Failed to fetch creator with ID ${id}`
    );
  }
  
  /**
   * Fetch creators for a specific playlist - Harmonized with channel approach
   */
  async getCreatorsForPlaylist(playlistId: string) {
    return this.executeQuery(
      async () => {
        console.log(`Fetching creators for playlist: ${playlistId}`);
        
        // First get derivatives with creator info for this playlist
        const { data: derivatives, error: derivativesError } = await supabase
          .from('derivatives_with_details')
          .select('creator_id, creator_name, creator_avatar')
          .eq('playlist_id', playlistId)
          .eq('status', 'approved');
          
        if (derivativesError || !derivatives || derivatives.length === 0) {
          console.log("No derivatives found for this playlist");
          return { data: [], error: null };
        }
        
        // Get unique creator IDs and store name/avatar mapping
        const creatorMap = new Map();
        derivatives.forEach(d => {
          if (d.creator_id) {
            creatorMap.set(d.creator_id, {
              name: d.creator_name,
              avatar: d.creator_avatar
            });
          }
        });
        
        const creatorIds = [...creatorMap.keys()];
        if (creatorIds.length === 0) {
          console.log("No creator IDs found");
          return { data: [], error: null };
        }
        
        console.log(`Found ${creatorIds.length} unique creators`);
        
        // Get creator profiles with additional info
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, full_name, username, avatar_url, expression_types, is_academy')
          .in('id', creatorIds);
          
        if (profilesError || !profiles) {
          console.error("Error fetching creator profiles:", profilesError);
          return { data: [], error: profilesError };
        }
        
        // Get derivative counts per creator 
        const { data: derivativeCounts, error: countsError } = await supabase
          .from('user_derivative_counts')
          .select('user_id, count')
          .in('user_id', creatorIds);
          
        // Create a map of user IDs to derivative counts
        const countMap = new Map();
        if (derivativeCounts) {
          derivativeCounts.forEach(item => {
            countMap.set(item.user_id, item.count);
          });
        }
        
        // Map creator data to desired format (harmonized with channel version)
        const mappedCreators = profiles.map(profile => {
          const creatorInfo = creatorMap.get(profile.id) || {};
          
          return {
            id: profile.id,
            name: profile.full_name || profile.username || creatorInfo.name || "Creator",
            fullName: profile.full_name,
            username: profile.username,
            avatarUrl: profile.avatar_url || creatorInfo.avatar,
            expressionTypes: profile.expression_types || [],
            isAcademy: profile.is_academy || false,
            // Provide both naming conventions for better compatibility
            creationsCount: countMap.get(profile.id) || 0,
            derivativesCount: countMap.get(profile.id) || 0
          };
        });
        
        return { data: mappedCreators, error: null };
      },
      `Failed to fetch creators for playlist ${playlistId}`
    );
  }

  /**
   * Unified method that works for both playlists and channels
   */
  async getCreatorsForContent(type: 'playlist' | 'channel', contentId: string) {
    if (type === 'playlist') {
      return this.getCreatorsForPlaylist(contentId);
    } else {
      return this.getCreatorsForChannel(contentId);
    }
  }

  /**
   * Fetch creators for a channel (existing implementation)
   */
  async getCreatorsForChannel(channelId: string) {
    // This is a placeholder for the existing implementation
    return this.executeQuery(
      async () => {
        // First get all derivatives for this channel to find unique creator IDs
        const { data: derivatives, error: derivativesError } = await supabase
          .rpc('get_derivatives_for_channel', { channel_uuid: channelId });
          
        if (derivativesError || !derivatives || derivatives.length === 0) {
          return { data: [], error: null };
        }
        
        // Get unique creator IDs
        const creatorIds = [...new Set(derivatives.filter(d => d.creator_id).map(d => d.creator_id))];
        
        if (creatorIds.length === 0) {
          return { data: [], error: null };
        }
        
        // Get creator profiles
        const { data: creators, error: creatorsError } = await supabase
          .from('profiles')
          .select('id, full_name, username, avatar_url, expression_types, is_academy')
          .in('id', creatorIds);
          
        if (creatorsError || !creators) {
          return { data: [], error: creatorsError };
        }
        
        // Get derivative counts per creator
        const { data: derivativeCounts, error: countsError } = await supabase
          .from('user_derivative_counts')
          .select('user_id, count')
          .in('user_id', creatorIds);
          
        // Create a map of user IDs to derivative counts
        const countMap = new Map();
        if (derivativeCounts) {
          derivativeCounts.forEach(item => {
            countMap.set(item.user_id, item.count);
          });
        }
        
        // Map creator data to desired format
        const mappedCreators = creators.map(creator => ({
          id: creator.id,
          name: creator.full_name || creator.username || "Creator",
          fullName: creator.full_name,
          username: creator.username,
          avatarUrl: creator.avatar_url,
          expressionTypes: creator.expression_types,
          isAcademy: creator.is_academy,
          creationsCount: countMap.get(creator.id) || 0,
          derivativesCount: countMap.get(creator.id) || 0
        }));
        
        return { data: mappedCreators, error: null };
      },
      `Failed to fetch creators for channel ${channelId}`
    );
  }

  /**
   * Increment profile view count
   */
  async incrementProfileView(profileId: string) {
    return this.executeQuery(
      async () => await supabase.rpc('increment_profile_views_secure', { profile_id: profileId }),
      `Failed to increment view count for profile ${profileId}`
    );
  }

  /**
   * Get creator profiles by array of IDs
   */
  async getProfilesByIds(creatorIds: string[]) {
    if (!creatorIds || creatorIds.length === 0) return [];
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, full_name, avatar_url')
        .in('id', creatorIds);
        
      if (error) {
        console.error('Error fetching creator profiles:', error);
        return [];
      }
      
      return data || [];
    } catch (err) {
      console.error('Unexpected error fetching creator profiles:', err);
      return [];
    }
  }
}

export const creatorsApi = new CreatorsApi();
